import { Component, DestroyRef, Input, OnChanges, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ToastService } from '../../services/toast.service';
import { Subscription } from 'rxjs';
import { NotificationRecipients, NotificationRecipientsResponse, NotificationRecipientsScope } from '../../models/recipients';
import { ApiService } from '../../services/api.service';
import { ResourceAccessService } from '../../services/resource-access.service';
import { sameRecipients } from '../../helpers/recipients';

const RECIPIENTS_SET_API: string = 'uv.notification.recipients.set.api';
const NO_RECIPIENTS: NotificationRecipients = { users: [], roles: [] };

/**
 * Who gets one kind of notification from a device: the list saved on the
 * device, or the default it inherits. Hidden when the user cannot read it,
 * read-only without the grant on recipients.set.
 */
@Component({
  selector: 'urban-recipients-editor',
  templateUrl: './recipients-editor.component.html',
  styleUrls: ['./recipients-editor.component.scss'],
  standalone: false,
  host: { '[hidden]': 'loaded() === false' },
})
export class RecipientsEditorComponent implements OnChanges {
  @Input({ required: true }) public deviceId!: string;
  /** Property holding the list, e.g. IntrusionRecipients; the BE default when omitted. */
  @Input() public scopeKey?: string;
  @Input({ required: true }) public title!: string;
  @Input() public hint?: string;
  /** Who is notified while no list applies, when that is not the whole domain. */
  @Input() public noneLabel: string = 'RECIPIENTS.SCOPE_NONE';

  private readonly api = inject(ApiService);
  private readonly toast = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly canEdit = inject(ResourceAccessService).canAccessSignal(RECIPIENTS_SET_API);
  /** Undefined while loading, false when the user cannot read the list. */
  protected readonly loaded = signal<boolean | undefined>(undefined);
  protected readonly scope = signal<NotificationRecipientsScope>('none');
  protected readonly recipients = signal<NotificationRecipients>(NO_RECIPIENTS);
  protected readonly dirty = signal<boolean>(false);
  protected readonly saving = signal<boolean>(false);

  private saved: NotificationRecipients = NO_RECIPIENTS;
  private request?: Subscription;

  public ngOnChanges(): void {
    this.load();
  }

  protected onChange(recipients: NotificationRecipients): void {
    this.recipients.set(recipients);
    this.dirty.set(!sameRecipients(recipients, this.saved));
  }

  protected discard(): void {
    this.recipients.set(this.saved);
    this.dirty.set(false);
  }

  /** An empty list drops the device's own list, so the default applies again. */
  protected useDefault(): void {
    this.persist(NO_RECIPIENTS);
  }

  protected save(): void {
    if (this.dirty()) {
      this.persist(this.recipients());
    }
  }

  private load(): void {
    if (!this.deviceId) {
      return;
    }
    this.request?.unsubscribe();
    this.request = this.api.getNotificationRecipients(this.deviceId, this.scopeKey)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(response => this.apply(response));
  }

  private apply(response: NotificationRecipientsResponse | null): void {
    this.loaded.set(response !== null);
    this.saved = {
      users: response?.Recipients?.Users ?? [],
      roles: response?.Recipients?.Roles ?? []
    };
    this.scope.set(response?.Scope ?? 'none');
    this.recipients.set(this.saved);
    this.dirty.set(false);
  }

  // Reloaded after the save: once the device list is dropped, only a read
  // tells whether a default applies or nobody is set.
  private persist(recipients: NotificationRecipients): void {
    if (this.saving()) {
      return;
    }
    this.saving.set(true);
    this.api.setNotificationRecipients(this.deviceId, recipients, this.scopeKey)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(response => {
        this.saving.set(false);
        if (response) {
          this.toast.done('RECIPIENTS.SAVED');
          this.load();
        }
      });
  }
}
